"use client";
import { useState, useEffect, useRef } from "react";
import { useScrollAnimate } from "./hooks";

const TEAL = "#4FCDC4";
const TEXT = "#EDEDED";

/* ── COUNT-UP ANIMATION ── */
export function CountUp({ to, suffix = "", duration = 1500, onDone }) {
  const decimals = String(to).includes(".") ? String(to).split(".")[1].length : 0;
  const [display, setDisplay] = useState(`${to}${suffix}`);
  const ref = useRef(null);
  const animated = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setDisplay("0");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated.current) {
          animated.current = true;
          const start = performance.now();
          const step = (now) => {
            const t = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
            const val = decimals > 0 ? eased * to : eased * (to - 1);
            const formatted = decimals > 0 ? val.toFixed(decimals) : String(Math.round(val));
            const midSuffix = suffix === "+" ? "" : suffix;
            setDisplay(t >= 1 ? `${to}${suffix}` : `${formatted}${midSuffix}`);
            if (t < 1) requestAnimationFrame(step);
            else if (onDoneRef.current) onDoneRef.current();
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [to, suffix, duration, decimals]);

  return <span ref={ref}>{display}</span>;
}

/* ── CHANGE BADGE (fades in after parent count finishes) ── */
export function ChangeBadge({ text, show, fontSize = 12, style = {} }) {
  return (
    <span style={{
      fontSize, fontWeight: 600, color: "#22c55e",
      opacity: show ? 1 : 0,
      transition: show ? "opacity 0.4s ease" : "none",
      ...style,
    }}>{text}</span>
  );
}

/* ── ANIMATED BAR ── */
export function AnimatedBar({ pct, color = TEAL, height = 3, delay = 0, opacity = 0.8, parentAnimated }) {
  const show = parentAnimated !== undefined ? parentAnimated : true;
  return (
    <div style={{
      width: show ? `${pct}%` : "0%",
      height: "100%", borderRadius: height, background: color, opacity,
      transition: show ? `width 1s cubic-bezier(.16,1,.3,1) ${delay}s` : "none",
    }} />
  );
}

/* ── ANIMATED DONUT ── */
export function AnimatedDonut({ score, r = 48, strokeWidth = 7, size = 108, onDone }) {
  const [ref, animated] = useScrollAnimate();
  const circ = 2 * Math.PI * r;
  const finalOff = circ * (1 - score / 100);
  return (
    <div ref={ref} style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={TEAL} strokeWidth={strokeWidth}
          strokeDasharray={circ} strokeDashoffset={animated ? finalOff : circ} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: animated ? "stroke-dashoffset 1.2s cubic-bezier(.16,1,.3,1)" : "none" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: TEXT, fontFamily: "var(--display)" }}>
          <CountUp to={score} duration={1200} onDone={onDone} />
        </span>
      </div>
    </div>
  );
}

/* ── ANIMATED LINE (SVG polyline draw-in) ── */
export function AnimatedLine({ points, stroke, strokeWidth = 2, opacity = 0.7, delay = 0, animated }) {
  const lineRef = useRef(null);
  const [length, setLength] = useState(600);
  useEffect(() => {
    if (lineRef.current) setLength(lineRef.current.getTotalLength());
  }, []);
  return (
    <polyline ref={lineRef} points={points} fill="none" stroke={stroke} strokeWidth={strokeWidth}
      opacity={opacity}
      strokeDasharray={length} strokeDashoffset={animated ? 0 : length}
      style={{ transition: animated ? `stroke-dashoffset 1.4s cubic-bezier(.16,1,.3,1) ${delay}s` : "none" }} />
  );
}
